import { InjectionToken } from '@angular/core';
import { Action } from '@ngrx/store';

/**
 * Injection token for injection options
 */
export const OPTIONS = new InjectionToken<NgrxStoreIdbOptions>('NgrxStoreIdb options');

/**
 * Name of the single record that held the whole marshalled state before the
 * per-slice record layout. Still written when `keys` is not configured
 * (marshaller mode), and read once for migration when it is.
 */
export const SAVED_STATE_KEY = 'State';

/**
 * Record-key prefix of the per-slice layout: each persisted top-level key is
 * stored under `State/<key>`, and entries of `perEntryKeys` slices under
 * `State/<key>/<entry>`, so a change to one slice never re-serialises the
 * others.
 */
export const SAVED_STATE_PREFIX = 'State/';

export const savedRecordKey = (name: string): string => SAVED_STATE_PREFIX + name;

export const savedEntryRecordKey = (name: string, entry: string): string =>
  `${SAVED_STATE_PREFIX}${name}/${entry}`;

export interface KeyConfiguration {
  [key: string]: string[] | number[] | KeyConfiguration[];
}

export type Keys = (KeyConfiguration | string)[];

/**
 * Configuration options for NgrxStoreIdb
 */
export interface NgrxStoreIdbOptions {
  /**
   * IndexDB configuration
   */
  idb: {
    /**
     * Builds the database name for a tenant. Each tenant gets its own
     * database (and therefore its own concurrency lock), so tabs running
     * different tenants can persist their state simultaneously.
     */
    dbNameForTenant: (tenantId: string) => string;
    /**
     * Store name
     */
    storeName: string;
    /**
     * Synchronously resolves the tenant id at bootstrap time, before the
     * NgRx store exists. Return null when the tenant is not yet known —
     * the store is then created lazily once the tenant resolver emits.
     */
    bootstrapTenantId: () => string | null;
  };
  /**
   * If true then store will be restored from IndexedDB on application startup
   */
  rehydrate: boolean;
  /**
   * Defines what slices of store should be stored/rehydrated.
   * Can not be defined if marshaller & unmarshaller are defined.
   * Default is null.
   */
  keys: Keys;
  /**
   * If defined then synchronisation of store -> IDB will be done only when the function returns true.
   * You can use it e.g. to do syncing only on certain action.
   */
  syncCondition: ((state: any, action: Action) => boolean) | null;
  /**
   * Method used to merge data loaded from IDB with Store state during rehydratation.
   * When null then default will be full deep merge. Must be used together with marshaller.
   * Can not be used together with keys.
   */
  unmarshaller: (state: any, rehydratedState: any) => any;
  /**
   * Method used to marshall store state into object to be written into IDB.
   * Must be used together with unmarshaller.
   * Can not be used together with keys.
   */
  marshaller: (state: any) => any;
  /**
   * Top-level keys (must also be listed in `keys` as plain strings) whose
   * entries are persisted as one record each (`State/<key>/<entry>`) instead
   * of one record for the whole slice. Use for large container slices such as
   * the @ngrx/data `entityCache`, where a change to one collection must not
   * re-serialise all of them.
   */
  perEntryKeys: string[];
  /**
   * Write coalescing. Saves are debounced by `debounceMs` (trailing edge) so
   * an action burst costs one IndexedDB write, and are forced through after
   * `maxWaitMs` so a continuous action stream still checkpoints.
   */
  flush: {
    debounceMs: number;
    maxWaitMs: number;
  };
  /**
   * Print debug info if true
   */
  debugInfo: boolean;
  /**
   * Configuration of concurrency options
   */
  concurrency: {
    /**
     * If false then library won't sync state to IndexedDB if it detects that another instance of
     * Window/Tab is already syncing.
     * Default is false.
     */
    allowed: boolean;
    /**
     * Time in ms how often library updates timestamp.
     * This shouldn't be less than 1000ms.
     * Default is 5000ms.
     */
    refreshRate?: number;
    /**
     * Name of key that holds the timestamp data.
     * Default is 'ConcurrencyTimestamp'.
     */
    trackKey?: string;
    /**
     * If the library detects that another instance of application already exists
     * (e.g. running in different tab/window) and this is set to true then application
     * won't start up.
     * Default if false.
     */
    failInitialisationIfNoLock: boolean;
  };
}
